import { MutableRefObject, useCallback, useEffect, useReducer, useRef } from 'react';
import {
  Appearance,
  Design,
  DesignSearchParams,
  DesignsResponse,
  PrintArea,
  ShopCollection,
} from '@/types';
import { assertNever } from '@/utils/assertNever';
import { fetchBlocklistCheck } from '@/utils/http/fetchBlocklistCheck';
import { fetchCYODesigns } from '@/utils/http/fetchCYODesigns';
import { fetchDesign } from '@/utils/http/fetchDesign';
import { fetchDesignForUser } from '@/utils/http/fetchDesignForUser';
import { fetchDesignsForUser } from '@/utils/http/fetchDesignsForUser';
import { fetchShopDesigns } from '@/utils/http/fetchShopDesigns';
import { isAborted } from '@/utils/http/methods/isAborted';
import { isAdminMode, isEmbroideryMode } from '@/utils/params';
import { mapDesignSearchResult } from '@/model/design/mapDesignSearchResult';
import { DesignSelectorTab } from '../DesignSelector';
import { FilterState } from '../DesignSelector';
import { usePrintabilityMap } from '../design-list-utils';

const REGEX_ID = /^\D?(\d{5,})(-\w)?$/;

type AbortControllerRef = MutableRefObject<AbortController | undefined>;

const stopRequest = (abortControllerRef: AbortControllerRef) => {
  abortControllerRef.current?.abort();
};

class BlockedSearchTermError extends Error {
  blocked: string[];

  constructor(blocked: string[]) {
    super(blocked.join());
    this.blocked = blocked;
  }
}

const checkAgainstBlocklist = async (term: string, abortControllerRef: AbortControllerRef) => {
  stopRequest(abortControllerRef);
  const abortController = new AbortController();
  abortControllerRef.current = abortController;
  const response = await fetchBlocklistCheck(term, 'SEARCH_TERMS', abortController.signal);

  if (!response) {
    return;
  }

  const blocked: string[] = [];
  for (const { blockedTerms } of response.results) {
    blocked.push(...blockedTerms);
  }

  if (blocked.length) {
    throw new BlockedSearchTermError(blocked);
  }
};

const initialState = {
  isActive: false,
  isLoading: false,
  isBlocklisted: false,
  designCount: -1,
  designs: [] as Design[],
  scrollKey: 0,
};

const ACTIVE_TYPE = 'active';

interface ActiveAction {
  type: typeof ACTIVE_TYPE;
}

const LOADING_TYPE = 'loading';

interface LoadingAction {
  type: typeof LOADING_TYPE;
}

const SET_LIST_TYPE = 'set-list';

interface SetListAction {
  type: typeof SET_LIST_TYPE;
  count: number;
  designs: Design[];
}

const APPEND_LIST_TYPE = 'append-list';

interface AppendListAction {
  type: typeof APPEND_LIST_TYPE;
  designs: Design[];
}

const EMPTY_LIST_TYPE = 'empty-list';

interface EmptyListAction {
  type: typeof EMPTY_LIST_TYPE;
}

const BLOCKLIST_CHECK_FAILED_TYPE = 'blocklist-failed';
interface BlockListCheckFailedAction {
  type: typeof BLOCKLIST_CHECK_FAILED_TYPE;
}

type Actions =
  | ActiveAction
  | LoadingAction
  | SetListAction
  | AppendListAction
  | EmptyListAction
  | BlockListCheckFailedAction;

const reducer = (state: typeof initialState, action: Actions) => {
  switch (action.type) {
    case ACTIVE_TYPE:
      return { ...state, isActive: true };
    case LOADING_TYPE:
      return { ...state, isLoading: true };
    case SET_LIST_TYPE:
      return {
        ...state,
        isLoading: false,
        designCount: action.count,
        designs: action.designs,
        resetScrollPosition: Date.now(),
        isBlocklisted: false,
      };
    case APPEND_LIST_TYPE:
      return { ...state, designs: state.designs.concat(action.designs) };
    case EMPTY_LIST_TYPE:
      return { ...state, designCount: -1, designs: [] };
    case BLOCKLIST_CHECK_FAILED_TYPE:
      return {
        ...state,
        isBlocklisted: true,
        designs: [] as Design[],
        isLoading: false,
        designCount: 0,
      };
    default:
      assertNever(action);
  }
};

export function useDesigns(
  visible: boolean,
  searchTerm: string,
  { safeSearch, showOnlyFreeDesigns, vectorDesigns }: FilterState,
  limit: number,
  selectedTab: DesignSelectorTab,
  selectedCollection: ShopCollection | undefined,
  printArea: PrintArea | undefined,
  appearance: Appearance
) {
  const [{ isActive, isLoading, designCount, designs, scrollKey, isBlocklisted }, dispatch] =
    useReducer(reducer, initialState);
  const scrollStartIndex = useRef(0);
  const printabilityMap = usePrintabilityMap(printArea, appearance, designs);

  const designAbortControllerRef = useRef<AbortController>();
  const blocklistAbortControllerRef = useRef<AbortController>();

  // cancel on unmount
  useEffect(() => {
    return () => {
      stopRequest(designAbortControllerRef);
      stopRequest(blocklistAbortControllerRef);
    };
  }, []);

  const fetchDesigns = useCallback(
    (offset: number): Promise<DesignsResponse> => {
      stopRequest(designAbortControllerRef);
      const abortController = new AbortController();
      designAbortControllerRef.current = abortController;
      const signal = abortController.signal;

      const idSearchMatch = REGEX_ID.exec(searchTerm);
      const designId = idSearchMatch ? idSearchMatch[1] : undefined;

      if (designId && isAdminMode()) {
        return fetchDesign(designId, { mode: 'edit' }, signal).then((design) => ({
          designs: [design],
          count: 1,
        }));
      }

      if (selectedTab === DesignSelectorTab.User) {
        if (designId) {
          return fetchDesignForUser(designId, signal).then((design) => ({
            designs: [design],
            count: 1,
          }));
        } else {
          return fetchDesignsForUser(limit, offset, signal);
        }
      }

      const params: DesignSearchParams = {
        limit,
        offset,
        term: designId ? undefined : searchTerm,
        designIds: designId,
        embroideryOnly: isEmbroideryMode(),
      };

      if (isEmbroideryMode()) {
        return fetchCYODesigns(params, signal).then(mapDesignSearchResult);
      }

      if (selectedTab === DesignSelectorTab.Community) {
        params.designType = showOnlyFreeDesigns ? 'FREE_ONLY' : 'ALL';
        params.safeSearch = safeSearch ? 'KIDS_ONLY' : 'ALL';
        params.imageType = vectorDesigns ? 'VECTOR' : 'ALL';

        return fetchCYODesigns(params, signal).then(mapDesignSearchResult);
      }

      if (selectedTab === DesignSelectorTab.Shop) {
        if (params.term === '') {
          params.orderBy = 'SORT_WEIGHT';
        }

        return fetchShopDesigns(
          {
            ...params,
            topicId: selectedCollection?.id,
          },
          signal
        ).then(mapDesignSearchResult);
      }

      assertNever(selectedTab);
    },
    [
      limit,
      safeSearch,
      searchTerm,
      showOnlyFreeDesigns,
      selectedTab,
      selectedCollection,
      vectorDesigns,
    ]
  );

  const handleTermChange = useCallback(async () => {
    dispatch({ type: LOADING_TYPE });

    try {
      const [, { designs, count }] = await Promise.all([
        checkAgainstBlocklist(searchTerm, blocklistAbortControllerRef),
        fetchDesigns(0),
      ]);

      dispatch({ type: SET_LIST_TYPE, count, designs });
      scrollStartIndex.current = 0;
    } catch (error) {
      if (isAborted(error)) {
        // ignore aborted requests
        return;
      }

      stopRequest(designAbortControllerRef);
      stopRequest(blocklistAbortControllerRef);

      if (error instanceof BlockedSearchTermError) {
        return dispatch({ type: BLOCKLIST_CHECK_FAILED_TYPE });
      }

      throw error;
    }
  }, [fetchDesigns, searchTerm]);

  useEffect(() => {
    if (!isActive && visible) {
      // set isActive only once
      // This prevents requesting designs before the modal gets opened the first time
      //  but also stops re-fetching when the only deps change is the visible state
      dispatch({ type: ACTIVE_TYPE });
    }
  }, [isActive, visible]);

  useEffect(() => {
    if (isEmbroideryMode()) {
      handleTermChange();
    } else {
      if (isActive) {
        if (selectedTab === DesignSelectorTab.Community && !searchTerm) {
          dispatch({ type: EMPTY_LIST_TYPE });
        } else {
          handleTermChange();
        }
      }
    }
  }, [isActive, searchTerm, selectedTab, handleTermChange]);

  const loadMoreItems = useCallback(
    (startIndex: number) => {
      return fetchDesigns(startIndex)
        .then(({ designs }) => {
          dispatch({ type: APPEND_LIST_TYPE, designs });
        })
        .catch((e) => {
          if (!isAborted(e)) {
            throw e;
          }
        });
    },
    [fetchDesigns]
  );

  return [
    isLoading,
    designCount,
    designs,
    scrollKey,
    scrollStartIndex,
    printabilityMap,
    loadMoreItems,
    isBlocklisted,
  ] as const;
}
